import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FixedSizeList as List } from 'react-window';

const API_URL = '/api/suppliers';

const TedarikciYonetimi = () => {
  const [tedarikciler, setTedarikciler] = useState([]);
  const [selectedTedarikci, setSelectedTedarikci] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [excelData, setExcelData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ ad: '', telefon: '', email: '', adres: '' });
  const [selectedParts, setSelectedParts] = useState(new Set());

  // TEDARİKÇİLERİ ÇEK + parcalar: [] garanti et
  useEffect(() => {
    const fetchTedarikciler = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(API_URL);
        if (!response.ok) throw new Error('Veri çekilemedi');
        const data = await response.json();

        // 🔥 Tüm tedarikçilerin parcalar'ını garantiye al
        const fixedData = data.map(t => ({
          ...t,
          parcalar: Array.isArray(t.parcalar) ? t.parcalar : []
        }));

        setTedarikciler(fixedData);

        // Seçili tedarikçi varsa güncelle
        if (selectedTedarikci) {
          const fresh = fixedData.find(t => t.id === selectedTedarikci.id);
          if (fresh) setSelectedTedarikci(fresh);
        }
      } catch (error) {
        console.error("Hata:", error);
        // Fallback
        const fallback = [
          {
            id: "1",
            ad: "Lupamat Kompresör Pazarlama A.Ş.",
            telefon: "+90 232 376 87 10",
            email: "lupamatpazarlama@lupamat.com",
            adres: "LA.O.S.B. 10036 Sok. No:3 35620 Çiğli/İzmir",
            parcalar: []
          }
        ];
        setTedarikciler(fallback);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTedarikciler();
  }, [selectedTedarikci?.id]); // Seçili tedarikçi değişirse yeniden yükle

  // EXCEL YÜKLE (readAsArrayBuffer ile)
  const handleExcelImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsLoading(true);
    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const arrayBuffer = evt.target.result;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(worksheet);

        setExcelData(data);
        toast.success(`${data.length} satır yüklendi. "Parçaları Ekle" butonuna basarak ekleyin.`);
      } catch (error) {
        console.error("Excel okuma hatası:", error);
        toast.error("Excel dosyası okunamadı. .xlsx formatında olmalı.");
      } finally {
        setIsLoading(false);
      }
    };

    reader.onerror = () => {
      toast.error("Dosya okunamadı.");
      setIsLoading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  // PARÇA EKLE (güncellenmiş)
  const addPartsToSupplier = async (supplierId) => {
    if (excelData.length === 0) {
      toast.warning("Lütfen önce Excel dosyası yükleyin");
      return;
    }
    if (!supplierId) {
      toast.error("Tedarikçi seçili değil");
      return;
    }

    try {
      setIsLoading(true);

      // 🔥 Tüm tedarikçilerin parcalar'ını güvenli hale getir
      const updatedSuppliers = tedarikciler.map(t => {
        const currentParts = Array.isArray(t.parcalar) ? t.parcalar : [];
        return t.id === supplierId
          ? { ...t, parcalar: [...currentParts, ...excelData] }
          : { ...t, parcalar: currentParts };
      });

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedSuppliers)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Hatası:", errorText);
        toast.error(`Kayıt başarısız: ${response.status}`);
        return;
      }

      // ✅ Hem liste hem seçili tedarikçi güncellenmeli
      setTedarikciler(updatedSuppliers);
      const updatedSelected = updatedSuppliers.find(t => t.id === supplierId);
      if (updatedSelected) setSelectedTedarikci(updatedSelected);

      setExcelData([]);
      toast.success(`${excelData.length} parça eklendi.`);
    } catch (error) {
      console.error("Parça ekleme hatası:", error);
      toast.error("Parça eklenemedi. Bağlantı veya format hatası.");
    } finally {
      setIsLoading(false);
    }
  };

  // YENİ TEDARİKÇİ EKLE
  const handleAddSupplier = async () => {
    if (!newSupplier.ad.trim()) {
      toast.warning("Tedarikçi adı gereklidir");
      return;
    }

    try {
      setIsLoading(true);
      const supplier = {
        id: Date.now().toString(),
        ...newSupplier,
        parcalar: [] // 🔥 Mutlaka olmalı
      };

      const updatedSuppliers = [...tedarikciler, supplier];
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedSuppliers)
      });

      if (!response.ok) throw new Error('Ekleme başarısız');

      setTedarikciler(updatedSuppliers);
      setNewSupplier({ ad: '', telefon: '', email: '', adres: '' });
      toast.success("Tedarikçi eklendi");
    } catch (error) {
      console.error("Ekleme hatası:", error);
      toast.error("Tedarikçi eklenemedi");
    } finally {
      setIsLoading(false);
    }
  };

  // TEK SATIR SİL
  const handleDeletePart = async (parcaKey) => {
    if (!selectedTedarikci) return;

    try {
      setIsLoading(true);
      const updatedParcalar = (selectedTedarikci.parcalar || []).filter(p => {
        const key = p?.['Parça Kodu'] || JSON.stringify(p);
        return key !== parcaKey;
      });

      const updatedSuppliers = tedarikciler.map(t =>
        t.id === selectedTedarikci.id ? { ...t, parcalar: updatedParcalar } : t
      );

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedSuppliers)
      });

      if (!response.ok) throw new Error('Silme başarısız');

      setTedarikciler(updatedSuppliers);
      setSelectedTedarikci({ ...selectedTedarikci, parcalar: updatedParcalar });
      toast.warn("Parça silindi");
    } catch (error) {
      toast.error("Silinemedi");
    } finally {
      setIsLoading(false);
    }
  };

  // TOPLU SİLME
  const handleBulkDelete = async () => {
    if (!selectedTedarikci || selectedParts.size === 0) return;

    try {
      setIsLoading(true);
      const updatedParcalar = (selectedTedarikci.parcalar || []).filter(p => {
        const key = p?.['Parça Kodu'] || JSON.stringify(p);
        return !selectedParts.has(key);
      });

      const updatedSuppliers = tedarikciler.map(t =>
        t.id === selectedTedarikci.id ? { ...t, parcalar: updatedParcalar } : t
      );

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedSuppliers)
      });

      if (!response.ok) throw new Error('Silme başarısız');

      setTedarikciler(updatedSuppliers);
      setSelectedTedarikci({ ...selectedTedarikci, parcalar: updatedParcalar });
      setSelectedParts(new Set());
      toast.warn(`${selectedParts.size} parça silindi`);
    } catch (error) {
      toast.error("Toplu silme başarısız");
    } finally {
      setIsLoading(false);
    }
  };

  // EXCEL'E AKTAR
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(selectedTedarikci?.parcalar || []);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Parçalar");
    XLSX.writeFile(wb, `${selectedTedarikci?.ad || 'tedarikci'}_parcalar.xlsx`);
  };

  // Filtrelenmiş parçalar
  const filteredParcalar = useMemo(() => {
    return (selectedTedarikci?.parcalar || []).filter(parca =>
      Object.values(parca).some(val =>
        String(val).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [selectedTedarikci?.parcalar, searchTerm]);

  const toggleSelectPart = (parcaKey) => {
    const newSelected = new Set(selectedParts);
    if (newSelected.has(parcaKey)) {
      newSelected.delete(parcaKey);
    } else {
      newSelected.add(parcaKey);
    }
    setSelectedParts(newSelected);
  };

  const handleDeleteSelected = () => {
    if (selectedParts.size === 0) {
      toast.info("Silinecek parça seçmediniz.");
      return;
    }
    handleBulkDelete();
  };

  // SANAL KAYDIRMA SATIRI
  const Row = ({ index, style }) => {
    const parca = filteredParcalar[index];
    const uniqueKey = parca?.['Parça Kodu'] || JSON.stringify(parca);

    return (
      <div style={style} className="flex border-b hover:bg-gray-50">
        <div className="w-12 p-2 flex items-center">
          <input
            type="checkbox"
            checked={selectedParts.has(uniqueKey)}
            onChange={() => toggleSelectPart(uniqueKey)}
          />
        </div>
        {Object.values(parca).map((val, i) => (
          <div key={i} className="flex-1 p-2 truncate" title={val}>
            {val}
          </div>
        ))}
        <div className="w-24 p-2 flex items-center">
          <button
            onClick={() => handleDeletePart(uniqueKey)}
            className="bg-red-500 text-white px-2 py-1 text-sm rounded hover:bg-red-600 w-full"
          >
            Sil
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <ToastContainer />
      <h1 className="text-3xl font-bold mb-6">Tedarikçi ve Parça Yönetimi</h1>

      {/* TEDARİKÇİ SEÇİMİ */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-2">Tedarikçi Seçin</h2>
        <select
          value={selectedTedarikci?.id || ""}
          onChange={(e) => {
            const tedarikci = tedarikciler.find(t => t.id === e.target.value) || null;
            setSelectedTedarikci(tedarikci);
          }}
          className="w-full border border-gray-300 p-2 rounded"
        >
          <option value="">Bir tedarikçi seçin</option>
          {tedarikciler.map((t) => (
            <option key={t.id} value={t.id}>
              {t.ad}
            </option>
          ))}
        </select>
      </div>

      {/* VERİ YÜKLE */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Veri Yükle</h2>
        <div className="flex flex-col md:flex-row gap-4">
          <label className="cursor-pointer flex items-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition">
            <span className="mr-2">📊</span>
            {isLoading ? 'Yükleniyor...' : 'Excel Yükle'}
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleExcelImport}
              disabled={isLoading}
              className="hidden"
            />
          </label>

          {excelData.length > 0 && (
            <button
              onClick={() => setExcelData([])}
              disabled={isLoading}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Temizle
            </button>
          )}
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Excel dosyasından veri yükleyin. Her satır dinamik olarak okunur.
        </p>
      </div>

      {/* TABLO */}
      {selectedTedarikci && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{selectedTedarikci.ad}</h2>
            <button
              onClick={exportToExcel}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              Excel'e Aktar
            </button>
          </div>

          <div className="mb-4">
            <input
              placeholder="Parça Ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>

          {selectedParts.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              disabled={isLoading}
              className="mb-4 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              {selectedParts.size} Parçayı Sil
            </button>
          )}

          {/* SANAL LİSTE (600px yükseklik) */}
          <div className="border rounded overflow-hidden" style={{ height: '600px' }}>
            {filteredParcalar.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                Henüz parça yok
              </div>
            ) : (
              <List
                height={600}
                itemCount={filteredParcalar.length}
                itemSize={45}
                itemData={filteredParcalar}
                width="100%"
              >
                {Row}
              </List>
            )}
          </div>

          {/* BUTONLAR (Aşağıda kalıcı olarak) */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => addPartsToSupplier(selectedTedarikci.id)}
              disabled={isLoading}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Parçaları Ekle
            </button>
            <button
              onClick={() => setExcelData([])}
              disabled={isLoading}
              className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
            >
              Temizle
            </button>
          </div>
        </div>
      )}

      {/* YENİ TEDARİKÇİ */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Yeni Tedarikçi Ekle</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            placeholder="Tedarikçi Adı"
            value={newSupplier.ad}
            onChange={(e) => setNewSupplier({ ...newSupplier, ad: e.target.value })}
            className="border border-gray-300 p-2 rounded"
            disabled={isLoading}
          />
          <input
            placeholder="Telefon"
            value={newSupplier.telefon}
            onChange={(e) => setNewSupplier({ ...newSupplier, telefon: e.target.value })}
            className="border border-gray-300 p-2 rounded"
            disabled={isLoading}
          />
          <input
            placeholder="Email"
            value={newSupplier.email}
            onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
            className="border border-gray-300 p-2 rounded"
            disabled={isLoading}
          />
          <input
            placeholder="Adres"
            value={newSupplier.adres}
            onChange={(e) => setNewSupplier({ ...newSupplier, adres: e.target.value })}
            className="border border-gray-300 p-2 rounded"
            disabled={isLoading}
          />
        </div>
        <button
          onClick={handleAddSupplier}
          disabled={isLoading}
          className="mt-4 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
        >
          Kaydet
        </button>
      </div>
    </div>
  );
};

export default TedarikciYonetimi;